<template>
  <div ref="masterStatusDropdown" class="relative flex max-h-8">
    <div
      v-tooltip="{
        content: $t('ORCHESTRATION.MASTER_STATUS_DESCRIPTION'),
        onShow: () => !menuOpened,
      }"
    >
      <lf-pill
        v-on-click-outside="closeMenu"
        :color-index="colorIndex"
        class="h-[24px] min-w-20 font-bold uppercase"
        :class="{ 'cursor-pointer': shouldDisplayMenu }"
        size="sm"
        @click.stop="toggleMenu"
      >
        <data-orchestration-master-status-dropdown-button
          :status="masterStatusType"
          :should-show-dropdown-icon="shouldDisplayMenu"
        />
      </lf-pill>
    </div>

    <div v-if="shouldDisplayMenu && menuOpened" class="absolute top-10">
      <ul
        id="masterStatusesMenu"
        class="master-statuses-menu absolute z-50 h-auto w-max overflow-auto rounded-md bg-white py-2 text-sm text-gray-500 shadow-lg"
      >
        <li
          v-for="status in nextPossibleStatuses"
          :key="status"
          class="cursor-pointer py-2 pr-2 text-xs font-semibold whitespace-nowrap uppercase hover:bg-gray-200"
          @click.stop="handleStatusChange(status)"
        >
          <span class="ml-3 cursor-pointer">
            {{ status }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import {
  DATA_ORCHESTRATION_MASTER_STATUS_NOT_STARTED,
  DATA_ORCHESTRATION_MASTER_STATUS_OPTIONS,
} from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import useApplicationsStore from "@/stores/applications";

import DataOrchestrationMasterStatusDropdownButton from "@/components/ui/DataOrchestrationMasterStatusDropdownButton.vue";

import type {
  IApplication,
  IDataOrchestrationLog,
} from "@/models/applications";
import type { IClient } from "@/models/clients";
import type {
  DataOrchestrationMasterStatus,
  DataOrchestrationMasterStatusType,
} from "@/models/orchestration";
import type { IRootState } from "@/models/state";
import type { PropType } from "vue";

const props = defineProps({
  model: {
    type: Object as PropType<IApplication>,
    default: null,
  },
  showDropdown: {
    type: Boolean,
    default: true,
  },
  log: {
    type: Object as PropType<IDataOrchestrationLog | null>,
  },
});

const { getters } = useStore<IRootState>();
const { isClientUser, isClientAnalyst, isClient, isLendflowUser } = useAuth();
const applicationsStore = useApplicationsStore();
const masterStatusDropdown = ref<HTMLElement | null>(null);
const authClientSettings = computed<IClient>(
  () => getters["auth/authClientSettings"]
);
const dataOrchestrationMasterStatuses = computed<
  Record<DataOrchestrationMasterStatusType, DataOrchestrationMasterStatus>
>(() => getters["options/dataOrchestrationMasterStatuses"]);
const menuOpened = ref(false);

const masterStatusType = computed(() => {
  if (props.log) {
    return props.log.master_status || 1;
  }
  return props.model?.data_orchestration_log?.master_status || 1;
});

const masterStatus = computed(
  () =>
    dataOrchestrationMasterStatuses.value[masterStatusType.value] ||
    DATA_ORCHESTRATION_MASTER_STATUS_NOT_STARTED
);

const colorIndex = computed(
  () => DATA_ORCHESTRATION_MASTER_STATUS_OPTIONS[masterStatus.value].color
);

const nextPossibleStatuses = computed(
  () =>
    DATA_ORCHESTRATION_MASTER_STATUS_OPTIONS[masterStatus.value]
      .transitions as DataOrchestrationMasterStatus[]
);

const shouldDisplayMenu = computed(() => {
  if (!props.showDropdown) {
    return false;
  }

  const canManageOrchestration =
    isLendflowUser ||
    (isClient &&
      authClientSettings.value.can_create_underwriter &&
      authClientSettings.value.can_manage_data_orchestration);

  const authPasses = !isClientUser && !isClientAnalyst;
  const hasPossibleStatuses = !!nextPossibleStatuses.value.length;

  return authPasses && hasPossibleStatuses && canManageOrchestration;
});

const masterStatusesMenuWidth = computed(() =>
  masterStatusDropdown.value
    ? masterStatusDropdown.value.clientWidth + "px"
    : null
);

const closeMenu = () => {
  menuOpened.value = false;
};

const toggleMenu = () => {
  if (!shouldDisplayMenu.value) {
    return;
  }

  if (!menuOpened.value) {
    document.body.click();
  }

  menuOpened.value = !menuOpened.value;
};

const handleStatusChange = (masterStatus: DataOrchestrationMasterStatus) => {
  const logId = props.log?.id || props.model?.data_orchestration_log?.id;

  if (!logId) {
    return;
  }

  const keys = Object.keys(dataOrchestrationMasterStatuses.value).map((key) =>
    Number(key)
  ) as DataOrchestrationMasterStatusType[];

  const masterStatusType = keys.find(
    (key) => dataOrchestrationMasterStatuses.value[key] === masterStatus
  );

  applicationsStore.updateDataOrchestrationMasterStatus({
    appId: props.model.id,
    master_status: Number(masterStatusType),
    logId,
  });

  closeMenu();
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.master-statuses-menu {
  min-width: v-bind(masterStatusesMenuWidth) !important;
}
</style>
